import { useApolloClient } from '@apollo/client';
import { useLatestCallback } from '@hover/blueprint';
import { ensureError } from '@hover/utilities';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { config } from 'src/config';
import { clearUserSession } from 'src/modules/homeownerApp/hooks/useUserSession';
import { LOCALSTORAGE_KEYS } from 'src/modules/properties/types/types';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useRevokeAccessTokenMutation } from 'src/shared/graphql/generated';
import { useLocation, useSplit } from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';
import { request } from 'src/shared/utils/request';
import { loginPathWithRedirectTo } from 'src/shared/utils/routing';

export interface UseLogoutProps {
  /**
   * Whether to include the `redirect_to` query parameter to redirect the user
   * back to where they were before logout upon logging back in.
   *
   * @defaultValue `true`
   */
  redirectTo?: boolean;
  /**
   * Whether to automatically redirect user to `/login` after successful logout.
   * This overrides the `redirectTo` setting.
   */
  redirect?: boolean;
  onSuccess?: () => void | Promise<void>;
  onError?: (error: Error) => void;
}

export const useLogout = (
  {
    redirectTo = true,
    redirect = true,
    onError = () => {},
    onSuccess = () => {},
  }: UseLogoutProps = {
    redirectTo: true,
  },
) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const apolloClient = useApolloClient();
  const { clearUser } = useSplit();
  const [revokeAccessTokenMutation] = useRevokeAccessTokenMutation();

  const [isPending, setIsPending] = useState(false);

  const onErrorLatest = useLatestCallback(onError);
  const onSuccessLatest = useLatestCallback(onSuccess);

  const logout = useCallback(async () => {
    try {
      setIsPending(true);

      // Clear the JWTs first, to prevent any race conditions where a request is made during this process
      // that refreshes the session or JWT.
      await revokeAccessTokenMutation();
      void defaultLocalStorageAdapter.removeAccessToken();
      void defaultLocalStorageAdapter.removeRefreshToken();

      await request(`${config.API_URI}/sessions`, {
        method: 'DELETE',
        credentials: 'include',
      });

      clearUserSession();

      try {
        await apolloClient.resetStore();
      } catch (_) {
        // This catches any authentication errors thrown when refetching the
        // user profile and other authenticated queries after logout
      }
      localStorage.removeItem(LOCALSTORAGE_KEYS.hover_jobs_viewableBy);

      await onSuccessLatest();
    } catch (cause) {
      const error = ensureError(cause);

      logError(error);
      onErrorLatest(error);
    } finally {
      clearUser();
      setIsPending(false);

      if (redirect) {
        history.push(
          redirectTo ? loginPathWithRedirectTo(pathname) : Route.LOGIN.path,
        );
      }
    }
  }, [
    apolloClient,
    history,
    onErrorLatest,
    onSuccessLatest,
    pathname,
    redirect,
    redirectTo,
    clearUser,
    revokeAccessTokenMutation,
  ]);

  return [logout, { isPending }] as const;
};
